const MIN_COLUMN_WIDTH = 48;
const states = new WeakMap();

function findTable(element) {
  return element.tagName === "TABLE" ? element : element.querySelector("table");
}

function getHeaderCells(table) {
  const headerRow = table.querySelector("thead tr");
  return headerRow ? Array.from(headerRow.children) : [];
}

function setColumnWidth(column, width) {
  const widthInPixels = `${Math.round(width)}px`;
  column.style.width = widthInPixels;
  column.style.minWidth = widthInPixels;
  column.style.maxWidth = widthInPixels;
}

function clearColumnWidth(column, defaultStyle) {
  column.style.width = defaultStyle?.width || "";
  column.style.minWidth = defaultStyle?.minWidth || "";
  column.style.maxWidth = defaultStyle?.maxWidth || "";
}

function updateHandleHeights(table) {
  const height = table.getBoundingClientRect().height;
  table.querySelectorAll(".column-resize-handle").forEach((handle) => {
    handle.style.height = `${height}px`;
  });
}

function normalizeOptions(binding) {
  const value = binding?.value || {};
  return typeof value === "object" ? value : {};
}

function getColumnConfig(options, index) {
  return Array.isArray(options.columns) ? options.columns[index] || {} : {};
}

function getColumnKey(column, index, options) {
  const config = getColumnConfig(options, index);
  return config.value || column.getAttribute("data-column-key") || String(index);
}

function getColumnMinWidth(column, index, options) {
  const config = getColumnConfig(options, index);
  const minWidth =
    config.minWidth ||
    options.minWidths?.[config.value] ||
    options.minWidths?.[index] ||
    column.getAttribute("data-min-width") ||
    options.minWidth ||
    MIN_COLUMN_WIDTH;

  const parsed = parseInt(minWidth, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : MIN_COLUMN_WIDTH;
}

function getStoredWidths(options, columns) {
  if (!options.storageKey) return null;

  try {
    const stored = JSON.parse(localStorage.getItem(options.storageKey));
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) {
      return null;
    }

    return columns.map((column, index) => {
      const key = getColumnKey(column, index, options);
      const value = Number(stored[key]);
      const minWidth = getColumnMinWidth(column, index, options);
      return Number.isFinite(value) && value >= minWidth ? value : null;
    });
  } catch (error) {
    return null;
  }
}

function persistWidths(options, columns) {
  if (!options.storageKey) return;

  const widths = columns.reduce((result, column, index) => {
    const key = getColumnKey(column, index, options);
    result[key] = Math.round(column.getBoundingClientRect().width);
    return result;
  }, {});

  try {
    localStorage.setItem(options.storageKey, JSON.stringify(widths));
  } catch (error) {
    // localStorage can be unavailable in private browsing or restricted contexts.
  }
}

function removeStoredWidths(options) {
  if (!options.storageKey) return;

  try {
    localStorage.removeItem(options.storageKey);
  } catch (error) {
    // Keep the table usable even when storage is blocked.
  }
}

function applyStoredWidths(table, columns, options) {
  const storedWidths = getStoredWidths(options, columns);
  if (!storedWidths || storedWidths.some((width) => width === null)) return;

  storedWidths.forEach((width, index) => {
    setColumnWidth(columns[index], width);
  });
  table.style.tableLayout = "fixed";
  table.style.width = `${storedWidths.reduce((total, width) => total + width, 0)}px`;
}

function prepareColumns(table, options) {
  const columns = getHeaderCells(table);
  const widths = columns.map((column) => column.getBoundingClientRect().width);

  columns.forEach((column, index) => {
    setColumnWidth(column, Math.max(widths[index], getColumnMinWidth(column, index, options)));
  });

  table.style.tableLayout = "fixed";
  table.style.width = `${widths.reduce((total, width) => total + width, 0)}px`;

  return columns;
}

function removeListeners(state) {
  state.listeners.forEach(({ element, event, handler }) => {
    element.removeEventListener(event, handler);
  });
  state.listeners = [];
}

function addListener(state, element, event, handler) {
  element.addEventListener(event, handler);
  state.listeners.push({ element, event, handler });
}

function restoreDefaultWidths(table, columns, state, options) {
  removeStoredWidths(options);
  columns.forEach((column, index) => {
    clearColumnWidth(column, state.defaultStyles[index]);
  });
  table.style.width = state.defaultTableWidth || "";
  table.style.tableLayout = state.defaultTableLayout || "";
}

function installHandles(element, binding) {
  const options = normalizeOptions(binding);
  const table = findTable(element);
  if (!table || options.disabled) return;

  let state = states.get(element);
  if (!state) {
    state = { listeners: [], frame: null, signature: "", resetKey: undefined };
    states.set(element, state);
  }

  const columns = getHeaderCells(table);
  const signature = columns.map((column) => column.textContent.trim()).join("|");
  const resetRequested =
    Object.prototype.hasOwnProperty.call(options, "resetKey") &&
    state.resetKey !== undefined &&
    state.resetKey !== options.resetKey;

  if (state.signature !== signature) {
    state.defaultStyles = columns.map((column) => ({
      width: column.style.width,
      minWidth: column.style.minWidth,
      maxWidth: column.style.maxWidth,
    }));
    state.defaultTableWidth = table.style.width;
    state.defaultTableLayout = table.style.tableLayout;
  }

  if (resetRequested) {
    restoreDefaultWidths(table, columns, state, options);
  }

  state.resetKey = options.resetKey;

  const expectedHandleCount = Math.max(columns.length - 1, 0);
  if (
    !resetRequested &&
    state.signature === signature &&
    table.querySelectorAll(".column-resize-handle").length === expectedHandleCount
  ) {
    updateHandleHeights(table);
    return;
  }

  removeListeners(state);
  table.querySelectorAll(".column-resize-handle").forEach((handle) => handle.remove());

  state.signature = signature;
  table.classList.add("resizable-columns-table");
  applyStoredWidths(table, columns, options);

  columns.slice(0, -1).forEach((column, index) => {
    const handle = document.createElement("span");
    handle.className = "column-resize-handle";
    handle.setAttribute("aria-hidden", "true");
    column.appendChild(handle);

    const startResize = (event) => {
      event.preventDefault();
      event.stopPropagation();

      const preparedColumns = prepareColumns(table, options);
      const currentColumn = preparedColumns[index];
      const nextColumn = preparedColumns[index + 1];
      if (!nextColumn) return;

      const startX = event.pageX;
      const currentWidth = currentColumn.getBoundingClientRect().width;
      const nextWidth = nextColumn.getBoundingClientRect().width;
      const currentMinWidth = getColumnMinWidth(currentColumn, index, options);
      const nextMinWidth = getColumnMinWidth(nextColumn, index + 1, options);

      handle.classList.add("is-resizing");
      document.body.classList.add("is-resizing-column");

      const resize = (moveEvent) => {
        let difference = moveEvent.pageX - startX;

        if (currentWidth + difference < currentMinWidth) {
          difference = currentMinWidth - currentWidth;
        }

        if (nextWidth - difference < nextMinWidth) {
          difference = nextWidth - nextMinWidth;
        }

        setColumnWidth(currentColumn, currentWidth + difference);
        setColumnWidth(nextColumn, nextWidth - difference);
      };

      const stopResize = () => {
        document.removeEventListener("mousemove", resize);
        document.removeEventListener("mouseup", stopResize);
        handle.classList.remove("is-resizing");
        document.body.classList.remove("is-resizing-column");
        updateHandleHeights(table);
        persistWidths(options, preparedColumns);
      };

      document.addEventListener("mousemove", resize);
      document.addEventListener("mouseup", stopResize);
    };

    addListener(state, handle, "mousedown", startResize);
  });

  updateHandleHeights(table);
}

function scheduleInstall(element, binding) {
  let state = states.get(element);
  if (!state) {
    state = { listeners: [], frame: null, signature: "", resetKey: undefined };
    states.set(element, state);
  }

  if (state.frame) cancelAnimationFrame(state.frame);
  state.frame = requestAnimationFrame(() => {
    state.frame = null;
    installHandles(element, binding);
  });
}

export default {
  inserted(element, binding) {
    scheduleInstall(element, binding);
  },
  componentUpdated(element, binding) {
    scheduleInstall(element, binding);
  },
  unbind(element) {
    const state = states.get(element);
    if (!state) return;

    if (state.frame) cancelAnimationFrame(state.frame);
    removeListeners(state);
    document.body.classList.remove("is-resizing-column");
    states.delete(element);
  },
};
